import { links } from "@/components/ads/config/burgerMenu";
import { logout } from "@/common/services/Auth";
import { useDeviceType } from "@/common/hooks";
import { deviceTypeEnum } from "@/common/utils/device";
import { useAppStore } from "@/common/store/appStore";
import { useCallback, useMemo, useRef, useState } from "react";
import { useUserStore } from "@/common/store/userStore";
import { routes } from "@/common/utils/routes";

import BlogIcon from "../images/icons/blog.svg?react";
import DownloadAppIcon from "../images/icons/download-app.svg?react";
import CamsIcon from "../images/icons/cam.svg?react";
import HelpIcon from "../images/icons/help.svg?react";
import SettingsIcon from "../images/icons/settings.svg?react";
import LogoutIcon from "../images/icons/logout.svg?react";
import GuidelinesIcon from "../images/icons/guidelines.svg?react";
import ExternalLinkIcon from "../images/icons/external-link.svg?react";
import ExperimentsIcon from "@/common/images/icons/plus.svg?react";

export const useBurgerMenu = () => {
  const [activeMenuItem, setActiveMenuItem] = useState<string>("");
  const toggleBurgerMenu = useAppStore((state) => state.toggleBurgerMenu);
  const isBurgerMenuOpen = useAppStore((state) => state.isBurgerMenuOpen);
  const user = useUserStore((state) => state.currentUser);
  const [devMode, setDevMode] = useState<boolean>(false);

  const isDesktop = useDeviceType() === deviceTypeEnum.DESKTOP;
  const devClickCounter = useRef(0);

  const handleCloseMenu = useCallback(() => {
    toggleBurgerMenu();
  }, [toggleBurgerMenu]);

  const onSetActiveMenuItem = useCallback((item: string) => {
    setActiveMenuItem(item);
  }, []);

  const handleDevModeClick = useCallback(() => {
    if (devMode) {
      return;
    }

    const waitTime = 2000;
    const clickThreshold = 10;

    if (devClickCounter.current === 0) {
      setTimeout(() => {
        devClickCounter.current = 0;
      }, waitTime);
    }

    devClickCounter.current++;

    if (devClickCounter.current >= clickThreshold) {
      setDevMode(true);
    }
  }, []);

  const menuItems = useMemo(() => ([
    {
      icon: CamsIcon,
      title: "RedGIFs Cams",
      rightIcon: ExternalLinkIcon,
      onClick: () => {
        window.location.href = isDesktop ? links.RGCamsDesktop : links.RGCams;
      },
    },
    {
      icon: DownloadAppIcon,
      title: "RedGIFs App",
      link: routes.app(),
    },
    {
      icon: BlogIcon,
      title: "Creator's Blog",
      link: "https://redgifs-creators.com/",
      target: "_blank",
      rel: "noopener",
      external: true,
      delimiter: true,
      rightIcon: ExternalLinkIcon,
    },
    {
      icon: HelpIcon,
      title: "FAQ",
      link: routes.faq(),
    },
    {
      icon: GuidelinesIcon,
      title: "User Policies",
      subNav: [
        {
          title: "Guidelines",
          link: routes.guidelines(),
        },
        {
          title: "Terms & Conditions",
          link: routes.terms(),
        },
        {
          title: "Privacy",
          link: routes.privacyPolicy(),
        },
        {
          title: "DMCA",
          link: routes.dmca(),
        },
        {
          title: "2257 Statement",
          link: routes.statement2257(),
        },
      ]
    },
    {
      icon: SettingsIcon,
      title: "Customise Cookie Preferences",
      className: "cky-banner-element",
    },
    ...(user ? [
      {
        icon: SettingsIcon,
        title: "Settings",
        link: routes.accountSettings(),
        delimiter: true,
      },
      {
        icon: LogoutIcon,
        title: "Log Out",
        className: "logoutMenuItem",
        onClick: () => {
          logout();
        }
      }
    ] : []),
    ...(devMode ? [
      {
        icon: ExperimentsIcon,
        title: "Experiments",
        link: routes.experiments(),
        onClick: () => {
          handleCloseMenu();
        }
      },
    ] : [])
  ]), [user, isDesktop, devMode]);

  return {
    onSetActiveMenuItem,
    handleCloseMenu,
    activeMenuItem,
    isBurgerMenuOpen,
    menuItems,
    handleDevModeClick
  };
};
